import { useState } from "react";
import { Star, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { http, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const ReviewForm = ({ token, defaultName, onDone }) => {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState(defaultName || "");
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData();
    fd.append("rating", String(rating));
    fd.append("title", title);
    fd.append("body", body);
    fd.append("name", name);
    if (photo) fd.append("photo", photo);
    try {
      await http.post(`/orders/${token}/review`, fd);
      toast.success("Thank you! Your review will appear once approved.");
      onDone();
    } catch (err) {
      toast.error(formatErr(err));
    }
    setLoading(false);
  };

  return (
    <form id="review" onSubmit={submit} data-testid="review-form" className="rounded-[28px] border border-[#E3D9CD] bg-[#FDFBF7] p-7">
      <p className="eyebrow text-[#C85A32]">Share your experience</p>
      <h2 className="font-display mt-2 text-3xl font-light">How's your Slowpour?</h2>
      <div className="mt-5 flex gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((i) => (
          <button type="button" key={i} data-testid={`review-star-${i}`} onClick={() => setRating(i)} onMouseEnter={() => setHover(i)} aria-label={`${i} stars`}>
            <Star size={28} className={i <= (hover || rating) ? "fill-[#E0A030] text-[#E0A030]" : "text-[#D9CFC3]"} />
          </button>
        ))}
      </div>
      <Input data-testid="review-title-input" placeholder="Title (e.g. My mornings are golden)" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-5 h-12 rounded-xl bg-white" required maxLength={120} />
      <Textarea data-testid="review-body-input" placeholder="What do you love? Any tips for other juicers?" value={body} onChange={(e) => setBody(e.target.value)} className="mt-3 min-h-[120px] rounded-xl bg-white" required maxLength={2000} />
      <Input data-testid="review-name-input" placeholder="Name to display (e.g. Maya R.)" value={name} onChange={(e) => setName(e.target.value)} className="mt-3 h-12 rounded-xl bg-white" required maxLength={60} />
      <label data-testid="review-photo-label" className="mt-3 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-[#D9CFC3] bg-white px-4 py-3 text-sm text-[#4A4543]">
        <ImagePlus size={18} className="text-[#C85A32]" />
        <span className="flex-1 truncate">{photo ? photo.name : "Add a photo of your pour (optional)"}</span>
        <input data-testid="review-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
      </label>
      <button data-testid="review-submit-button" disabled={loading} className="mt-6 flex items-center gap-2 rounded-full bg-[#181615] px-7 py-3.5 text-sm font-semibold text-[#FDFBF7] transition-[background-color] duration-300 hover:bg-[#C85A32] disabled:opacity-60">
        {loading && <Loader2 size={16} className="animate-spin" />} Submit review
      </button>
    </form>
  );
};
